import { Target, Handshake, Cog, Eye } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Reveal, SectionHead } from "../components/site/Motion";
import { ClientMarquee } from "../components/home/ClientMarquee";
import { CtaBand } from "../components/home/CtaBand";
import { Seo, crumbsLd } from "../components/site/Seo";

const VALUES = [
  [Target, "Precision", "Parts made to drawing, checked to drawing — every lot."],
  [Handshake, "Partnership", "Long-term supply relationships with OEMs and distributors."],
  [Cog, "Ownership", "In-house compounding, molding and finishing under one roof."],
  [Eye, "Transparency", "Clear communication on samples, timelines and capacity."],
];

const SCALE = [
  ["2012", "Year established"],
  ["80+", "Clients across India"],
  ["09", "Product families"],
  ["B2B", "RFQ-based pricing"],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo path="/about" title="About Autocrat Rubber Industries | Since 2012, Gurugram" description="Since 2012, Autocrat Rubber Industries in Gurugram has supplied rubber components to 80+ clients across India, including Luminous, Okaya and Akira." jsonLd={crumbsLd([["Home", "/"], ["About", "/about"]])} />
      <PageHero testId="about-hero" video eyebrow="About Autocrat" image="/media/factory_press.jpg" lines={["Built on", { text: "rubber.", className: "text-outline" }]} crumbs={[{ to: "/", label: "Home" }, { label: "About" }]}>
        Autocrat Rubber Industries was founded in 2012 to give Indian manufacturers a dependable, quality-first source for rubber components.
      </PageHero>

      <section className="bg-white py-24 md:py-32">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Our story" title="From a single press to India's OEM floors." />
          </div>
          <Reveal className="space-y-5 text-sm leading-relaxed text-slate-600 md:text-base lg:col-span-7" delay={0.1}>
            <p>Autocrat started in 2012 with a clear focus: manufacture rubber parts that fit right the first time and arrive when promised. Over the years, that focus earned the trust of more than 80 clients across India — including leading battery and power brands like Luminous, Okaya, Akira, Ajanta, Ruchira Green Earth, Longway, Bos JRG and StesaLIT.</p>
            <p>Today we manufacture molded rubber parts, extruded profiles, grommets, bushes, gaskets, seals, O-rings and fully custom components — developed from customer drawings or samples and supplied at production scale.</p>
            <p className="text-xs text-slate-400">[Add founder name, plant size, workforce and capacity details here via the admin team.]</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-scale" className="bg-[#07090e] py-20 text-white">
        <div className="container-x grid grid-cols-2 gap-y-10 md:grid-cols-4">
          {SCALE.map(([v, k], i) => (
            <Reveal key={k} delay={i * 0.08} className={`${i ? "md:border-l md:border-slate-800 md:pl-8" : ""}`}>
              <p className="font-display text-6xl font-black text-steel-grad md:text-8xl">{v}</p>
              <p className="eyebrow mt-2 text-slate-400">{k}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-24 md:py-32">
        <div className="container-x">
          <SectionHead eyebrow="Values" title="What we stand on." />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div data-testid={`value-card-${i}`} className="clip-corner-sm h-full border border-slate-200 bg-white p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#0a6cff]">
                  <Icon className="h-7 w-7 text-[#0a6cff]" strokeWidth={1.5} />
                  <h3 className="font-display mt-8 text-3xl font-extrabold uppercase text-slate-950">{t}</h3>
                  <p className="mt-2 text-sm text-slate-600">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-vision" className="bg-white py-24 md:py-32">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <p className="eyebrow text-[#0a6cff] lg:col-span-3">Vision</p>
          <Reveal className="lg:col-span-9">
            <p className="font-display text-4xl font-bold uppercase leading-[0.98] text-slate-950 sm:text-5xl lg:text-6xl">
              To be the most trusted rubber component partner for Indian manufacturers — <span className="text-[#0a6cff]">known for precision, consistency and long-term partnership.</span>
            </p>
          </Reveal>
        </div>
      </section>
      <ClientMarquee />
      <CtaBand />
    </div>
  );
}
